import {
  AfterViewInit,
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Chart, ChartDataset, TimeScaleOptions, TimeUnit } from "chart.js/auto";
import { FloatLabelModule } from "@openng/optimus-ui/floatlabel";
import { DecimalPipe, NgClass } from "@angular/common";
import { DateTime, DateTimeUnit } from "luxon";

import { firstValueFrom } from "rxjs";
import { ASSET_TYPES } from "../../../../../enums/assetTypes.enum";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { MultiSelectModule } from "@openng/optimus-ui/multiselect";
import { createTimeoutSignal } from "../../../../../shared/utils/timeout.utils";
import { TimeoutComponent } from "../../../../../shared/components/timeout/timeout.component";
import { ChartChangeTimeEvent } from "../../../../../models/ui/chartData";
import { BarChartData, DataPoint } from "../../../../../models/ui/energyToolCharts";
import { DropdownOption } from "../../../../../models/ui/dropdownOption";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { DatePickerModule, DatePickerTypeView } from "@openng/optimus-ui/datepicker";
import { TIMEFRAME_OPTIONS } from "../../../../../constants/timeFrameOptions";

@Component({
  standalone: true,
  selector: "app-bar-chart",
  imports: [
    DatePickerModule,
    FormsModule,
    TranslateModule,
    FloatLabelModule,
    NgClass,
    ProgressSpinnerModule,
    MultiSelectModule,
    FormsModule,
    TimeoutComponent,
  ],
  templateUrl: "./bar-chart.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./bar-chart.component.scss",
})
export class BarChartComponent implements AfterViewInit, OnDestroy, OnChanges {
  public sourceType = input<string>();
  public sourceId = input<string>();
  public chartData = input<BarChartData[] | null>();
  public chartId = input<string>();
  private barChart!: Chart<"bar" | "line", DataPoint[]>;
  public date: Date = new Date();
  public calendarView: DatePickerTypeView = "month";
  public dateFormat = "mm.yy";
  public currentFilterAggregation: DateTimeUnit = "month";
  public filterAggregationOptions: DropdownOption[] = TIMEFRAME_OPTIONS;

  public label!: string;
  public dataSets!: BarChartData[];

  private translate = inject(TranslateService);

  public loading = computed(() => this.chartData() === null);
  public timeOut = createTimeoutSignal(this.loading);

  private decimalPipe = inject(DecimalPipe);

  @Output() changeTimeframeEvent = new EventEmitter<ChartChangeTimeEvent>();

  constructor() {
    effect(async () => {
      const datasets = this.chartData();
      if (datasets) {
        this.dataSets = datasets;
        //TODO: Problem Here: label is not always at the same index. source needs to return label of selected asset as own property
        if (datasets.length > 0) {
          this.label = datasets[0].label ?? "";
        }

        const barDataSets: ChartDataset<"bar" | "line", DataPoint[]>[] = [];
        datasets.forEach((dataset, index: number) => {
          if (dataset.data) {
            if (dataset.type === "line") {
              const lineSet: ChartDataset<"line", DataPoint[]> = {
                label: dataset.label,
                data: dataset.data,
                order: dataset.order,
                type: dataset.type,
                pointRadius: dataset.pointRadius,
                borderWidth: dataset.borderWidth,
                borderColor: dataset.borderColor,
              };
              barDataSets.push(lineSet);
            } else {
              const barSet: ChartDataset<"bar", DataPoint[]> = {
                label: dataset.label,
                data: dataset.data,
                backgroundColor: dataset.backgroundColor,
                hidden: false,
                order: dataset.order,
              };
              if (index > 1) {
                barSet.hidden = true;
              }
              barDataSets.push(barSet);
            }
          }
        });
        this.barChart.data.datasets = barDataSets;
        this.setXAxisOptions();
        await this.updateLang(this.translate.currentLang);
        this.barChart.update();
      }
    });
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe({
      next: async () => {
        await this.updateLang(this.translate.currentLang);
      },
    });
  }
  public ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      const sourceIdChanged = changes["sourceId"] && !changes["sourceId"].firstChange;
      const sourceTypeChanged = changes["sourceType"] && !changes["sourceType"].firstChange;
      if (sourceIdChanged || sourceTypeChanged) {
        this.currentFilterAggregation = "month";
        this.dateFormat = "mm.yy";
        this.date = new Date();
      }
    }
  }
  public ngOnDestroy() {
    if (this.barChart) {
      this.barChart.destroy();
    }
  }

  public ngAfterViewInit() {
    const chartId = this.chartId();
    if (!chartId) {
      return;
    }

    this.barChart = new Chart(chartId, {
      type: "bar",
      data: {
        datasets: [],
      },
      options: {
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            labels: {
              color: "#ffffff",
            },
          },
          tooltip: {
            callbacks: {
              label: (context: any) => {
                const label = context.dataset.label;
                const value = this.decimalPipe.transform(
                  context.parsed.y.toFixed(2),
                  undefined,
                  this.translate.currentLang
                );

                return `${label}: ${value} kWh`;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              color: "#727272",
            },

            border: {
              display: false,
            },
            ticks: {
              color: "#ffffff",
            },
          },
          x: {
            adapters: {
              date: {
                locale: this.translate.currentLang,
              },
            },
            type: "timeseries",
            time: {
              unit: "day",
              tooltipFormat: "dd. MMM",
              displayFormats: {
                hour: "HH:mm",
                day: "d",
                month: "MMM",
              },
            },
            grid: {
              color: "#727272",
            },
            ticks: {
              color: "#ffffff",
            },
          },
        },
      },
    });
  }
  /**
   * Updates the bar chart by translating the label for the average energy loss and
   * updating the locale of the x-axis.
   * @param lang The locale to use for the translation.
   */
  private async updateLang(lang: string) {
    const translations = await firstValueFrom(this.translate.get(["energy_tool.bar_chart.average"]));
    if (this.sourceType() === ASSET_TYPES.GATE_SYSTEM) {
      const noOfDatasets = this.barChart.data.datasets.length;
      if (noOfDatasets > 0)
        this.barChart.data.datasets[noOfDatasets - 1].label = translations["energy_tool.bar_chart.average"];
    }
    this.updateTimeseriesLocale(this.barChart, "x", lang);
    this.barChart.update();
  }

  /**
   * Updates the locale of a timeseries axis on a chart.
   * @param chart The chart to update.
   * @param axis The axis to update.
   * @param locale The locale to use for the axis.
   */
  private updateTimeseriesLocale(chart: Chart<"bar" | "line", DataPoint[]>, axis: "x" | "y", locale: string) {
    const scale = chart?.options?.scales?.[axis];
    if (scale?.type === "timeseries") {
      const ts = scale as TimeScaleOptions;
      ts.adapters = {
        date: { locale },
      };
    }
  }

  /**
   * Updates the unit of a timeseries axis on a chart.
   * @param chart The chart to update.
   * @param axis The axis to update.
   * @param unit The unit to use for the axis.
   */
  private updateTimeseriesUnit(chart: Chart<"bar" | "line", DataPoint[]>, axis: "x" | "y", unit: TimeUnit) {
    const scale = chart?.options?.scales?.[axis];
    if (scale?.type === "timeseries") {
      const ts = scale as TimeScaleOptions;
      ts.time.unit = unit;
    }
  }

  /**
   * Updates the tooltip format of a timeseries axis on a chart.
   * @param chart The chart to update.
   * @param axis The axis to update.
   * @param format The format to use for the tooltip.
   */
  private updateTimeseriesTooltipFormat(chart: Chart<"bar" | "line", DataPoint[]>, axis: "x" | "y", format: string) {
    const scale = chart?.options?.scales?.[axis];
    if (scale?.type === "timeseries") {
      const ts = scale as TimeScaleOptions;
      ts.time.tooltipFormat = format;
    }
  }

  /**
   * Updates the display format of a timeseries axis on a chart.
   * @param chart The chart to update.
   * @param axis The axis to update.
   * @param format The format to use for the display.
   */
  private updateTimeseriesDisplayFormat(chart: Chart<"bar" | "line", DataPoint[]>, axis: "x" | "y", format: string) {
    const scale = chart?.options?.scales?.[axis];
    if (scale?.type === "timeseries") {
      const ts = scale as TimeScaleOptions;
      ts.time.displayFormats["day"] = format;
    }
  }

  /**
   * Configures the x-axis of the bar chart based on the current filter aggregation.
   * - For "day": Sets time unit to "hour" and tooltip format to "HH:mm".
   * - For "week": Sets time unit to "day", display format to "EEE dd.MM.", and tooltip format to "dd.MM.yyyy".
   * - For "month": Sets time unit to "day", display format to "d", and tooltip format to "dd. MMM".
   * - For "year": Sets time unit to "month" and tooltip format to "MMM yyyy".
   */

  private setXAxisOptions() {
    switch (this.currentFilterAggregation) {
      case "day":
        this.updateTimeseriesUnit(this.barChart, "x", "hour");
        this.updateTimeseriesTooltipFormat(this.barChart, "x", "HH:mm");
        break;
      case "week":
        this.updateTimeseriesUnit(this.barChart, "x", "day");
        this.updateTimeseriesDisplayFormat(this.barChart, "x", "EEE dd.MM.");
        this.updateTimeseriesTooltipFormat(this.barChart, "x", "dd.MM.yyyy");
        break;
      case "month":
        this.updateTimeseriesUnit(this.barChart, "x", "day");
        this.updateTimeseriesDisplayFormat(this.barChart, "x", "d");
        this.updateTimeseriesTooltipFormat(this.barChart, "x", "dd. MMM");
        break;
      case "year":
        this.updateTimeseriesUnit(this.barChart, "x", "month");
        this.updateTimeseriesTooltipFormat(this.barChart, "x", "MMM yyyy");
        break;
    }
  }

  /**
   * Called when an aggregation option is clicked.
   * Sets the current filter aggregation, calendar view, and date format accordingly.
   * Also calls setTimeWindow() to update the time window based on the new filter aggregation.
   * @param option The value of the clicked aggregation option.
   */
  public clickAggregationOption(option: string | number | boolean) {
    this.currentFilterAggregation = option as DateTimeUnit;
    switch (option) {
      case "day":
        this.calendarView = "date";
        this.dateFormat = "dd.mm.yy";
        break;
      case "week":
        // To display KW (week of year) set date Format to the formated value (via luxon) and on dateChange, prev and next window . so everytime date changes
        this.calendarView = "date";
        this.dateFormat = "dd.mm.yy";
        break;
      case "month":
        this.calendarView = "month";
        this.dateFormat = "mm.yy";
        break;
      case "year":
        this.calendarView = "year";
        this.dateFormat = "yy";
        break;
    }

    this.setTimeWindow();
    this.date = new Date(this.date);
  }

  /**
   * Move the time window backward by one unit of the current filter aggregation.
   * Then calls setTimeWindow() to update the time window.
   */
  public previousTimeWindow() {
    this.date = DateTime.fromMillis(this.date.getTime())
      .minus({
        [this.currentFilterAggregation]: 1,
      })
      .toJSDate();

    this.setTimeWindow();
  }

  /**
   * Move the time window forward by one unit of the current filter aggregation.
   * Then calls setTimeWindow() to update the time window.
   */
  public nextTimeWindow() {
    this.date = DateTime.fromMillis(this.date.getTime())
      .plus({
        [this.currentFilterAggregation]: 1,
      })
      .toJSDate();

    this.setTimeWindow();
  }

  /**
   * Set the time window by finding the start and end timestamps of the current filter aggregation
   * based on the current date.
   * Then emit the changeTimeframeEvent with the start, end, time frame, and asset labels.
   */
  public setTimeWindow() {
    const start = DateTime.fromMillis(this.date.getTime())
      .startOf(this.currentFilterAggregation as DateTimeUnit)
      .toMillis();
    const end = DateTime.fromMillis(this.date.getTime())
      .endOf(this.currentFilterAggregation as DateTimeUnit)
      .toMillis();
    const labels = this.barChart.data.datasets.reduce((acc: string[], dataset) => {
      if (dataset.label) {
        acc.push(dataset.label);
      }
      return acc;
    }, []);

    this.changeTimeframeEvent.emit({
      start: start,
      end: end,
      timeFrame: this.currentFilterAggregation,
      assetLabels: labels,
    });
  }
}
